import { useState, useEffect, useContext } from 'react';
import { FreightCardGridView } from './FreightCardGridView';
import {
  ETransportFreightPlutoDto,
  GetAdminFreightsParams,
  GetAdminTargetsParams,
  GetFreightsParams,
  MinimalTarget,
  PlutoCustomerDto,
  PlutoFreightResponseDto,
} from '@/generated/model';
import {
  getAdminFreights,
  getAdminTargets,
  getETransportCustomers,
  getFreights,
  getTargets1,
} from '@/generated/client/etransport/etransport';
import { TargetFreightsDetail } from './TargetFreightsDetails';
import { FreightDataChange } from '@/utilities/etransportTypes';
import { TargetFreightsSubmitForm } from './TargetFreightsSubmitForm';
import { FreightSubmitForm } from './FreightSubmitForm';
import { Dialog } from '@mui/material';
import { isPhoneOrHighScreen } from '@/utilities/sizeUtils';
import { ctxUser } from '@/contexts/UserContext';

import './etransport.css';

export default function EtransportPopup({ close }: { close: () => void }) {
  // const { t } = useTranslation();
  const userCtx = useContext(ctxUser);

  const [freights, setFreights] = useState<PlutoFreightResponseDto | undefined>(undefined);
  const [targets, setTargets] = useState<MinimalTarget[] | undefined>(undefined);
  const [customers, setCustomers] = useState<PlutoCustomerDto[] | undefined>(undefined);
  const [customer, setCustomer] = useState<PlutoCustomerDto | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [newChangesAdded, setNewChangesAdded] = useState<FreightDataChange | null>(null);
  const [openTargetFreightSubmitForm, setOpenTargetFreightSubmitForm] = useState<boolean>(false);
  const [openTargetFreightDetails, setOpenTargetFreightDetails] = useState<boolean>(false);
  const [openFreightSubmitForm, setOpenFreightSubmitForm] = useState<boolean>(false);
  const [currentFreight, setCurrentFreight] = useState<ETransportFreightPlutoDto | undefined>(undefined);
  const [searchTarget, setSearchTarget] = useState<string | undefined>(undefined);
  const [refetchTrigger, setRefetchTrigger] = useState<number>(0);
  const [isClosingDetails, setIsClosingDetails] = useState<boolean>(false);
  const [isClosingSubmitForm, setIsClosingSubmitForm] = useState<boolean>(false);

  const isAdmin = userCtx?.user.isSuperAdmin;

  function updateCurrentFreight(freights: PlutoFreightResponseDto) {
    const groups = [freights.active, freights.inactive, freights.future];
    for (const group of groups) {
      const match = group?.freights?.find((freight) => freight.uit === currentFreight?.uit);
      if (match) {
        setCurrentFreight(match);
        return;
      }
    }
    setCurrentFreight(undefined);
  }

  async function fetchData(updateFreight: boolean) {
    setIsLoading(true);
    try {
      if (isAdmin) {
        const customersResult = await getETransportCustomers();
        const customersList = customersResult?.data.customers ?? [];
        setCustomers(customersList);
        let defaultCustomer: PlutoCustomerDto;
        if (!customer) {
          defaultCustomer = customersList[0];
          setCustomer((prev) => prev ?? defaultCustomer);
        } else {
          defaultCustomer = customer;
        }

        const customerId = defaultCustomer?.id ? String(defaultCustomer.id) : '';
        const [freightsResult, targetsResult] = await Promise.all([
          getAdminFreights({ customerId } satisfies GetAdminFreightsParams),
          getAdminTargets({ customerId } satisfies GetAdminTargetsParams),
        ]);
        const freights: PlutoFreightResponseDto = freightsResult.data;
        setTargets(targetsResult.data);
        setFreights(freights);
        if (updateFreight) updateCurrentFreight(freights);
      } else {
        const [freightsResult, targetsResult] = await Promise.all([getFreights(), getTargets1()]);
        const freights: PlutoFreightResponseDto = freightsResult.data;
        setTargets(targetsResult.data);
        setFreights(freights);
        if (updateFreight) updateCurrentFreight(freights);
      }
    } catch (err) {
      setFreights(undefined);
      setTargets(undefined);
      setCustomers(undefined);
      if (updateFreight) setCurrentFreight(undefined);
      console.error('Error fetching data:', err);
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    fetchData(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (refetchTrigger) fetchData(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refetchTrigger]);

  useEffect(() => {
    if (searchTarget === undefined && customer == undefined) return;

    async function fetchFilteredFreights() {
      setIsLoading(true);

      try {
        if (isAdmin) {
          const customerId = customer?.id ? String(customer.id) : '';
          const [freightsResult, targetsResult] = await Promise.all([
            getAdminFreights({ filter: searchTarget, customerId } satisfies GetAdminFreightsParams),
            getAdminTargets({ customerId } satisfies GetAdminTargetsParams),
          ]);
          const freights: PlutoFreightResponseDto = freightsResult.data;
          setTargets(targetsResult.data);
          setFreights(freights);
        } else {
          const freightsResult = await getFreights({ filter: searchTarget } satisfies GetFreightsParams);
          const freights: PlutoFreightResponseDto = freightsResult.data;
          setFreights(freights);
        }
      } catch (err) {
        setFreights(undefined);
        console.error('Error fetching data:', err);
      } finally {
        setIsLoading(false);
      }
    }

    fetchFilteredFreights();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTarget, customer]);

  function handleRefetch(): void {
    setRefetchTrigger((prev) => prev + 1);
  }

  return isPhoneOrHighScreen() ? (
    <Dialog fullScreen open={true} onClose={close} className="popup-base-whole" hideBackdrop={true}>
      <div style={{ position: 'relative', width: '100%', height: '100%' }}>
        <div
          className="etransport-freight-panel etransport-freight-panel-overlay etransport-overlay-base phone"
          data-qa-element-id="etr_freight_card_grid"
        >
          <FreightCardGridView
            targets={targets}
            freights={freights}
            customer={customer}
            customers={customers}
            isLoading={isLoading}
            searchTarget={searchTarget}
            currentFreight={currentFreight}
            onRefetch={handleRefetch}
            setCustomer={setCustomer}
            setNewChanges={setNewChangesAdded}
            setSearchTarget={setSearchTarget}
            setCurrentFreight={setCurrentFreight}
            setOpenFreightSubmitForm={setOpenFreightSubmitForm}
            setOpenTargetFreightDetails={setOpenTargetFreightDetails}
            setOpenTargetFreightSubmitForm={setOpenTargetFreightSubmitForm}
          />
        </div>

        {(openTargetFreightDetails || isClosingDetails) && (
          <div
            className={`etransport-freight-panel etransport-freight-panel-overlay etransport-overlay-container phone ${!openTargetFreightDetails && isClosingDetails ? 'closing' : ''}`}
            data-qa-element-id="etr_freight_detail_panel"
          >
            {openTargetFreightSubmitForm ? (
              <TargetFreightsSubmitForm
                uit={currentFreight?.uit}
                targets={targets}
                minDate={new Date((currentFreight?.validityStartTimeMicros || 0) / 1000)}
                maxDate={new Date((currentFreight?.validityEndTimeMicros || 0) / 1000)}
                setNewChanges={setNewChangesAdded}
                setOpenTargetFreightSubmitForm={setOpenTargetFreightSubmitForm}
              />
            ) : (
              <TargetFreightsDetail
                targets={targets}
                freight={currentFreight}
                newChange={newChangesAdded}
                openTargetFreightSubmitForm={openTargetFreightSubmitForm}
                onRefetch={handleRefetch}
                setNewChanges={setNewChangesAdded}
                setCurrentFreight={setCurrentFreight}
                setIsClosingDetails={setIsClosingDetails}
                setOpenTargetFreightDetails={setOpenTargetFreightDetails}
                setOpenTargetFreightSubmitForm={setOpenTargetFreightSubmitForm}
              />
            )}
          </div>
        )}

        {(openFreightSubmitForm || isClosingSubmitForm) && (
          <div
            className={`etransport-freight-panel etransport-freight-panel-overlay etransport-overlay-container phone ${!openFreightSubmitForm && isClosingSubmitForm ? 'closing' : ''}`}
            data-qa-element-id="etr_freight_submit_panel"
          >
            <FreightSubmitForm
              targets={targets}
              onRefetch={handleRefetch}
              setCurrentFreight={setCurrentFreight}
              setIsClosingSubmitForm={setIsClosingSubmitForm}
              setOpenFreightSubmitForm={setOpenFreightSubmitForm}
            />
          </div>
        )}
      </div>
    </Dialog>
  ) : (
    <div style={{ position: 'relative', width: '500px' }}>
      <div
        className="etransport-freight-panel etransport-freight-panel-overlay etransport-overlay-base"
        data-qa-element-id="etr_freight_card_grid"
      >
        <FreightCardGridView
          targets={targets}
          freights={freights}
          customer={customer}
          customers={customers}
          isLoading={isLoading}
          searchTarget={searchTarget}
          currentFreight={currentFreight}
          onRefetch={handleRefetch}
          setCustomer={setCustomer}
          setNewChanges={setNewChangesAdded}
          setSearchTarget={setSearchTarget}
          setCurrentFreight={setCurrentFreight}
          setOpenFreightSubmitForm={setOpenFreightSubmitForm}
          setOpenTargetFreightDetails={setOpenTargetFreightDetails}
          setOpenTargetFreightSubmitForm={setOpenTargetFreightSubmitForm}
        />
      </div>
      {(openTargetFreightDetails || isClosingDetails) && (
        <div
          className={`etransport-overlay-container ${!openTargetFreightDetails && isClosingDetails ? 'closing' : ''}`}
        >
          <div
            className="etransport-freight-panel etransport-freight-panel-overlay"
            data-qa-element-id="etr_freight_detail_panel"
          >
            <TargetFreightsDetail
              targets={targets}
              freight={currentFreight}
              newChange={newChangesAdded}
              openTargetFreightSubmitForm={openTargetFreightSubmitForm}
              onRefetch={handleRefetch}
              setNewChanges={setNewChangesAdded}
              setCurrentFreight={setCurrentFreight}
              setIsClosingDetails={setIsClosingDetails}
              setOpenTargetFreightDetails={setOpenTargetFreightDetails}
              setOpenTargetFreightSubmitForm={setOpenTargetFreightSubmitForm}
            />
          </div>
          {openTargetFreightSubmitForm && (
            <div className="etransport-freight-panel" data-qa-element-id="etr_freight_target_submit_panel">
              <TargetFreightsSubmitForm
                uit={currentFreight?.uit}
                targets={targets}
                minDate={new Date((currentFreight?.validityStartTimeMicros || 0) / 1000)}
                maxDate={new Date((currentFreight?.validityEndTimeMicros || 0) / 1000)}
                setNewChanges={setNewChangesAdded}
                setOpenTargetFreightSubmitForm={setOpenTargetFreightSubmitForm}
              />
            </div>
          )}
        </div>
      )}
      {(openFreightSubmitForm || isClosingSubmitForm) && (
        <div
          className={`etransport-overlay-container ${!openFreightSubmitForm && isClosingSubmitForm ? 'closing' : ''}`}
        >
          <div
            className="etransport-freight-panel etransport-freight-panel-overlay"
            data-qa-element-id="etr_freight_submit_panel"
          >
            <FreightSubmitForm
              targets={targets}
              onRefetch={handleRefetch}
              setCurrentFreight={setCurrentFreight}
              setIsClosingSubmitForm={setIsClosingSubmitForm}
              setOpenFreightSubmitForm={setOpenFreightSubmitForm}
            />
          </div>
        </div>
      )}
    </div>
  );
}
